<template>
  <div>
    <b-skeleton-wrapper :loading="isLoading">
      <template #loading>
        <b-card class="mb-1">
          <template #header>
            <b-skeleton width="70%" />
          </template>
          <b-skeleton width="50%" />
        </b-card>
      </template>
      <div v-if="suggestion" class="mb-3">
        <small>Suggested rewrite:</small>
        <div class="page_title">{{suggestion}}</div>
      </div>
      <div v-if="isNoData" class="mb-4">
        <b-icon-exclamation-triangle-fill variant="warning" />
        The {{ page.queries.length }}
        {{ page.queries.length != 1 ? 'queries' : 'query' }}
        for this page didn't provide enough data to score the title.
      </div>
      <b-card no-body v-for="reason in reasons" :key="reason.id" class="mb-1">
        <b-card-header>
          <div class="d-flex justify-content-between align-items-center">
            <template v-if="reason.ignored">
              <span v-html="reason.title" class="text-muted" />
              <b-link
                @click="toggleIgnore(reason)"
                class="text-small text-muted"
                >Un-mute</b-link
              >
            </template>
            <template v-else>
              <div>
                <b-icon-check-circle-fill
                  v-if="reason.ok"
                  class="text-success mr-1"
                />
                <strong><span v-html="reason.title" /></strong>
                <b-icon-info-circle
                  v-if="reason.tooltip && reason.tooltip()"
                  v-b-tooltip="reason.tooltip()"
                  class="ml-1 text-small"
                />
              </div>
              <b-link
                v-if="reason.ignorable"
                @click="toggleIgnore(reason)"
                class="text-small text-muted"
                >Mute</b-link
              >
            </template>
          </div>
        </b-card-header>
        <b-card-body
          v-if="
            reason.id != 'success' &&
            !reason.ignored &&
            (!is_demo || !reason.ok)
          "
        >
          <div v-if="reason.id == 'missing'">
            <span>{{ reason.prefix }}&nbsp;</span>
            <span v-for="word in reason.values" :key="word" translate="no">
              <b-badge
                pill
                href="#"
                @click.prevent="addWord(word)"
                variant="danger"
                class="mr-1 query_word"
                >{{ word }} +</b-badge
              >
            </span>
            <span>{{ reason.suffix }}</span>
            <span>
              Sample phrase{{ phrases.length != 1 ? 's' : '' }}:
              <span v-for="(phrase, i) in phrases" :key="i">
                <code translate="no" class="query_query">{{
                  phrase.query
                }}</code>
                <span v-html="i < phrases.length - 1 ? ', ' : ''" />
              </span>
            </span>
          </div>
          <div v-else-if="reason.id == 'boilerplate'">
            <span>{{ reason.prefix }}&nbsp;</span>
            <span
              v-for="fragment in reason.values"
              :key="fragment"
              translate="no"
            >
              <b-badge
                pill
                href="#"
                @click.prevent="removeFragment(fragment)"
                variant="danger"
                class="mr-1 query_word"
                >{{ fragment }} −</b-badge
              >
            </span>
            <span>{{ reason.suffix }}</span>
          </div>
          <div v-else-if="reason.id == 'rewritten'">
            <b-popover v-if="page.serp_result" :target="`serp-${page.id}`" triggers="hover focus" custom-class="serp-popover">
              <template #title>
                <div class="d-flex justify-content-between">
                  <div v-if="page.serp_result.data?.screenshot">
                    SERP Screenshot
                  </div>
                  <div v-else>
                    SERP Preview
                  </div>
                  <div class="text-small font-weight-normal">
                    {{page.serp_result.updated_at | relativeTime}}
                  </div>
                </div>
              </template>
              <b-img v-if="page.serp_result.data?.screenshot" :src="`https://siteseer.io/serp/${page.serp_result.data.screenshot}`" />
              <template v-else>
                <h4 :style="{color: '#1a0dab', fontSize:'20px', fontWeight: 'normal', margin:0}">{{page.serp_result.title}}</h4>
                <p>{{page.serp_result.description}}</p>
              </template>
            </b-popover>
            <b-link @click="openGoogleWindow()" :id="`serp-${page.id}`">Google SERPs <b-icon-box-arrow-up-right/></b-link>
            show a rewritten title:
            <div class="page_title">
              <span class="pr-1"
                ><Score v-if="serp_score !== null" :value="serp_score"
              /></span>
              <Diff
                :old="live_title || ''"
                :new="reason.values[0] || ''"
                :one-line="true"
              />
            </div>
          </div>
          <div v-else v-html="reason.fail"></div>
          <blockquote v-if="reason.id.startsWith('heading') && headingWords.length" class="blockquote mb-0 mt-2">
            <span
              v-for="(word, i) of headingWords"
              :key="i"
              :class="
                (word.labels.includes('ranked') ? 'text-decoration-underline success' : '') + ' page_title_word'
              "
            >
              {{ word.word }}
            </span>
          </blockquote>
        </b-card-body>
      </b-card>
    </b-skeleton-wrapper>
    <div
      v-if="lastCrawledAt"
      class="text-small my-2 d-flex align-items-left align-items-baseline"
    >
      <div class="mr-1">Last Google Crawl:</div>
      <div>{{ lastCrawledAt }}</div>
    </div>
  </div>
</template>
<style>
.badge .b-icon {
  margin-right: -3px;
}
.serp-popover {
  width: 640px;
  min-width: 640px;
}
</style>
<script>
import _ from 'lodash'
import Diff from '@/components/Diff'
import Score from '@/components/Score'
import SERP_QUERY from '@/graphql/SerpQuery.gql'
import INSPECT_URL_QUERY from '@/graphql/InspectUrlQuery.gql'
import TITLE_SUGGESTION_QUERY from '@/graphql/PageTitleSuggestionQuery.gql'

import {
  BIconInfoCircle,
  BIconCheckCircleFill,
  BIconExclamationTriangleFill
} from 'bootstrap-vue'

const MIN_COUNT = 5

export default {
  name: 'TitleReasons',
  components: {
    Diff,
    Score,
    BIconInfoCircle,
    BIconCheckCircleFill,
    BIconExclamationTriangleFill
  },
  props: [
    'site',
    'page',
    'title',
    'live_title',
    'serp_score',
    'is_demo',
  ],

  apollo: {
    gsc_inspect_url: {
      query: INSPECT_URL_QUERY,
      variables() {
        return {
          page_id: this.title.id
        }
      },
      skip() {
        return true
      },
      fetchPolicy: 'cache-first'
    },
    suggestion: {
      query: TITLE_SUGGESTION_QUERY,
      variables() {
        return {
          title: this.title.text,
          words: this.title.query_words.filter(e => !e.ignored && e.score > process.env.VUE_APP_WORD_CLICK_THRESHOLD).map(e => e.word),
          language: this.page.language || this.site.language
        }
      },
      skip() {
        console.log(this.isLoading, !this.title.reasons.some(e => ['missing', 'too_far', 'too_long', 'too_short'].includes(e.id)))
        return this.isLoading || !this.title.reasons.some(e => ['missing', 'too_far', 'too_long', 'too_short'].includes(e.id))
      }
    }
  },

  data: () => {
    return {
      gsc_inspect_url: {},
      suggestion: null,
    }
  },
  computed: {
    isLoading() {
      return (
        !this.title.reasons ||
        (this.$route &&
          this.$route.params.page_id &&
          this.$route.params.page_id != this.title.id)
      )
    },
    phrases() {
      if (this.isLoading) return []
      const missing = this.title.reasons.find((e) => e.id == 'missing')
      if (!missing || !missing.values) {
        return []
      }
      const queries = this.title.query_words.filter((e) =>
        missing.values.includes(e.word)
      )
      return _.uniqBy(queries, 'query').slice(0, 3)
    },
    lastCrawledAt() {
      return this.gsc_inspect_url.last_crawled_at
        ? new Date(this.gsc_inspect_url.last_crawled_at).toLocaleString()
        : null
    },
    isNoData() {
      return this.isLoading ? null : this.title.reasons.some((e) => e.id == 'no_data')
    },
    reasons() {
      let reasons = (this.title.reasons || []).concat(_.get(this.title, 'heading.reasons', []))
        .map(({ id, values }) => {
          const formatted = values.map((e) =>
            (e || '').length > 70
              ? `<code title="${e}" class="query_word">${e.slice(
                  0,
                  70
                )}…</code>`
              : `<code class="query_word">${e}</code>`
          )
          const s = values.length !== 1 ? 's' : ''
          const list = [_.initial(formatted).join(', ')]
            .concat([_.last(formatted)])
            .filter((e) => e)
            .join(' and ')
          const reasons = {
            missing: {
              title: 'Add Top Keywords',
              success: 'Nice work! All your top keywords appear in the title.',
              prefix: `Include`,
              suffix: (this.$route && this.$route.params.page_id)
                ? `or you can mute ${s ? 'them' : 'it'} in the sidebar.`
                : 'in your page title.',
              tooltip: () => ({
                title:
                  'We analyze all the top phrases you rank for in order to determine your most important keywords.'
              }),
              ignorable: true
            },
            too_far: {
              title: `Place Top Keywords Near the Front`,
              success:
                'Keeping your keywords visible near the front of the title can improve click-through rates.',
              fail: `The keyword${s} ${list} may not be visible to all users. Placing ${
                s ? 'them' : 'it'
              } earlier may improve click-through rates.`,
              tooltip: () => ({
                title:
                  'Front-loading keywords near the beginning of your title tag is a great way to ensure visibility.'
              }),
              ignorable: true
            },
            boilerplate: {
              title: 'Remove Boilerplate',
              success: "Your title doesn't contain irrelevant boilerplate.",
              prefix: `Your site uses`,
              suffix: `repeatedly, but ${
                s ? "they aren't" : "it isnt't"
              } a top keyword phrase for this URL. Removing ${
                s ? 'them' : 'it'
              } may have a positive impact.`,
              tooltip: () => ({
                title: `"Boilerplate" refers to text that's used repeatedly across your site. In titles, this is often your brand name. Experiments show that removing boilerplate when it's irrelevant may improve performance.`
              }),
              ignorable: true
            },
            duplicate: {
              title: 'Make Title Unique',
              success: 'The title is 100% unique to your site!',
              fail: `Each title should be unique. This exact title is also found on ${list}.`,
              tooltip: () => ({
                title:
                  'Google discourages duplicate titles. Unique titles often have a better chance of ranking.'
              }),
              ignorable: true
            },
            non_html: {
              title: 'Non-HTML URL',
              fail: `This page was not scored because it is a ${values[0]}.`
            },
            too_long: {
              title: `Length Warning`,
              success: 'Your title length is good.',
              fail: `Your title length of ${values[0]}px is over Google's desktop display limit, meaning it is likely to be truncated.`,
              variant: 'warning',
              icon: 'exclamation-triangle-fill'
            },
            too_short: {
              title: `Length Warning`,
              fail: `Your title length of ${values[0]} characters might be too short. Consider rewriting it to be more descriptive.`,
              variant: 'warning',
              icon: 'exclamation-triangle-fill'
            },
            rewritten: {
              title: `Google Rewriting`,
              success: `Google SERPs show your exact title`,
              variant: 'warning',
              icon: 'exclamation-triangle-fill',
              tooltip: () => {
                if (!_.get(this, 'page.serp_result')) {
                  return false
                }
                return {
                  title: `Collected on ${this.$options.filters.formatDate(
                    this.page.serp_result.updated_at
                  )}`
                }
              }
            },
            placeholder_title: {
              title: `Replace the default title on this page`,
              fail: `It looks like the title was not updated from the default template. Rewrite it to be more descriptive.`,
              variant: 'warning',
              icon: 'exclamation-triangle-fill'
            },
            heading_empty: {
              title: 'Add a Top-Level Heading to this Page',
              fail: `Our crawler did not find an H1 on this page. It is recommended you include a unique heading on every page`,
              ignorable: true,
              values: ['']
            },
            heading_missing: {
              title: 'Consider adding Top Keywords to the H1',
              fail: `Including ${list} might make your top-level heading more descriptive.`,
              ignorable: true
            },
            heading_boilerplate: {
              title: 'Remove Boilerplate from H1',
              fail: `The page's top-level heading contains ${list} which is used in several title tags across the site`,
              tooltip: () => ({
                title: `"Boilerplate" refers to text that's used repeatedly across your site. In titles, this is often your brand name. Experiments show that removing boilerplate when it's irrelevant may improve performance.`
              }),
              ignorable: true
            },
            heading_duplicate: {
              title: 'Make your H1 Unique',
              fail: `Top-level headings should ideally be unique, and not exactly match title tags for other pages: ${list}.`,
              ignorable: true
            },
            heading_too_short: {
              title: `H1 might be too short`,
              fail: `Your H1 length of ${values[0]} characters might be too short. Consider rewriting it to be more descriptive.`,
              variant: 'warning',
              icon: 'exclamation-triangle-fill'
            },
          }
          return { id, values, ...reasons[id]}
        }).map(e => ({...e, ok: !e.values || !e.values.length })) // allow reasons.value to be overriden above
        .filter(
          (r) =>
            r.title &&
            (this.is_demo
              ? !['rewritten', 'duplicate', 'boilerplate'].includes(r.id)
              : !r.ok)
        )
      if (!reasons.length) {
        reasons = [
          {
            id: 'success',
            values: [],
            title: 'Nice work! Your title looks optimized',
          }
        ]
      }
      if(this.headingWords.length && !reasons.some(e => e.id.startsWith('heading'))) {
        reasons.push({
            id: 'heading_success',
            values: [],
            title: 'Top-Level Heading',
          })
      }
      reasons.forEach((r) => {
        r.ignorable = !this.is_demo && r.ignorable
        const pi = this.page.page_issues.find((pi) => pi.name == r.id)
        if (pi) {
          r.ignored = pi.ignored
        }
      })
      return reasons
    },
    headingWords() {
      return _.flatMap(_.get(this.title, 'heading.fragments', []), 'words')
    }
  },

  methods: {
    openGoogleWindow() {
      _zyppy.track('SERP Opened')
      const url =
        'https://www.google.com/search?' +
        new URLSearchParams({ q: 'site:' + this.page.url }).toString()
      const options = {
        height: 480,
        width: 720,
        popup: true
      }
      options.left = window.screen.width / 2 - options.width / 2
      options.top = window.screen.height / 2 - options.height / 2
      window.open(
        url,
        'google',
        Object.entries(options)
          .map(([k, v]) => k + '=' + v)
          .join(',')
      )
    },
    removeFragment(str) {
      _zyppy.track('Fragment Removed')
      const title = document
        .querySelector('.title-edit')
        .innerText.replace(str, '')
      this.$emit('update', title)
    },
    addWord(str) {
      _zyppy.track('Word Added')
      const $el = document.querySelector('.title-edit')
      const text = $el.innerText
      let start = 0
      let end = 0
      try {
        const selection = document.getSelection()
        const range = selection.getRangeAt(0)
        start = range.startOffset
        end = range.endOffset
      } catch (e) {
        console.error(e)
      }
      if (start > 0 && !/\W/.test(text.charAt(start - 1))) {
        str = ' ' + str
      }
      if (end < text.length && !/\W/.test(text.charAt(end))) {
        str = str + ' '
      }
      $el.innerHTML =
        text.substring(0, start) +
        '<span>' +
        str +
        '</span>' +
        text.substring(end, text.length)
      try {
        let selection = window.getSelection()
        selection.removeAllRanges()
        let node
        $el.children.forEach(function ($child) {
          if ($child.innerText.trim() == str) {
            node = $child
          }
        })
        if (node) {
          let range = document.createRange()
          range.selectNode(node)
          selection.addRange(range)
        }
      } catch (e) {
        console.error(e)
        console.log($el)
      }
      this.$emit('update', $el.innerText)
    },
    toggleIgnore(reason) {
      this.$emit('ignore', reason.id, reason.ignored)
      _zyppy.track(`Issue ${reason.ignored ? 'Un-' : ''}Ignored`, {
        name: reason.id,
        page_id: this.page.id,
        type: 'title'
      })
    }
  }
}
</script>